import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../config";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  getDocs,
  query,
  where,
  setDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify"; // Import react-toastify
import "react-toastify/dist/ReactToastify.css"; // Import react-toastify CSS
import Nav from "../Components/Nav";

function InvestmentFlow() {
  const [step, setStep] = useState("planSelection"); // planSelection, funding, confirmation
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState("");
  const [fundingSource, setFundingSource] = useState(""); // balance or paymentMethod
  const [selectedMethod, setSelectedMethod] = useState("");
  const [walletAddresses, setWalletAddresses] = useState({});
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [messages, setMessages] = useState({}); // Step-specific messages
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setBalance(userDoc.data().balance || 0);
            setUsername(userDoc.data().fullname || currentUser.email); // Use email if username is not set
          }

          const walletRef = doc(db, "settings", "walletAddresses");
          const walletSnapshot = await getDoc(walletRef);
          if (walletSnapshot.exists()) {
            setWalletAddresses(walletSnapshot.data());
          }

          const querySnapshot = await getDocs(
            collection(db, "investmentPlans")
          );
          const fetchedPlans = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPlans(fetchedPlans);
        } catch (error) {
          console.error("Error fetching data:", error);
          setMessages({ global: "Error fetching user data or plans." });
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    // Clear messages after 3 seconds
    if (messages[step]) {
      const timer = setTimeout(() => {
        setMessages((prev) => ({ ...prev, [step]: "" }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [messages, step]);

  useEffect(() => {
    // Show toast for confirmation messages
    if (messages.confirmation) {
      if (
        messages.confirmation.includes("success") ||
        messages.confirmation.includes("copied")
      ) {
        toast.success(messages.confirmation, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error(messages.confirmation, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  }, [messages.confirmation]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setAmount(plan.min.toString()); // Set default amount to plan minimum
    setStep("funding");
  };

  const handleFundingSubmit = (e) => {
    e.preventDefault();
    const investmentAmount = parseFloat(amount);

    if (!fundingSource) {
      setMessages((prev) => ({
        ...prev,
        funding: "Please select a funding source.",
      }));
      return;
    }

    if (isNaN(investmentAmount) || investmentAmount <= 0) {
      setMessages((prev) => ({
        ...prev,
        funding: "Please enter a valid amount.",
      }));
      return;
    }

    if (investmentAmount < selectedPlan.min) {
      setMessages((prev) => ({
        ...prev,
        funding: `Amount must be at least $${selectedPlan.min}.`,
      }));
      return;
    }

    if (
      selectedPlan.max !== "Unlimited" &&
      investmentAmount > selectedPlan.max
    ) {
      setMessages((prev) => ({
        ...prev,
        funding: `Amount cannot exceed $${selectedPlan.max}.`,
      }));
      return;
    }

    if (fundingSource === "paymentMethod" && !selectedMethod) {
      setMessages((prev) => ({
        ...prev,
        funding: "Please select a payment method.",
      }));
      return;
    }

    setStep("confirmation");
  };









const handleConfirm = async () => {
  setSubmitting(true);
  const investmentAmount = parseFloat(amount);

  try {
    // Check for existing active investment (only relevant for balance funding)
    if (fundingSource === "balance") {
      const investmentsRef = collection(db, "investments");
      const activeQuery = query(
        investmentsRef,
        where("userId", "==", user.uid),
        where("plan", "==", selectedPlan.name),
        where("status", "==", "active")
      );
      const activeSnapshot = await getDocs(activeQuery);

      // if (!activeSnapshot.empty) {
      //   setMessages((prev) => ({
      //     ...prev,
      //     confirmation: `You already have an active investment in the "${selectedPlan.name}" plan.`,
      //   }));
      //   setSubmitting(false);
      //   return;
      // }
    }

    // Balance check only for funding from balance
    if (fundingSource === "balance" && investmentAmount > balance) {
      setMessages((prev) => ({
        ...prev,
        confirmation: "Insufficient balance.",
      }));
      setSubmitting(false);
      return;
    }

    if (fundingSource === "balance") {
      // === Original behavior restored ===
      // Create investment with status "active"
      const investmentData = {
        userId: user.uid,
        email: user.email,
        username: username,
        plan: selectedPlan.name,
        roi: selectedPlan.roi,
        duration: selectedPlan.duration,
        amount: investmentAmount,
        createdAt: new Date(),
        status: "active", // immediately active
      };

      await addDoc(collection(db, "investments"), investmentData);

      // Deduct from user balance
      const userDocRef = doc(db, "users", user.uid);
      const newBalance = balance - investmentAmount;
      await updateDoc(userDocRef, { balance: newBalance });
      setBalance(newBalance);

      setMessages((prev) => ({
        ...prev,
        confirmation: "Investment activated successfully!",
      }));
    } else {
      // === fundingSource !== "balance" (payment method) ===
      // Only create a deposit request — NO investment entry yet

      const depositData = {
        userId: user.uid,
        userEmail: user.email,
        username: username,
        method: selectedMethod,
        amount: investmentAmount,
        timestamp: new Date().toISOString(),
        status: "pending",
        plan: selectedPlan.name, // helpful for admin later
        roi: selectedPlan.roi,
        duration: selectedPlan.duration,
        // investmentId will be added later when confirmed
      };

      const depositId = `${user.uid}_${Date.now()}`;
      await setDoc(doc(db, "deposits", depositId), depositData);

      // Send deposit request email
      await axios.post("https://renderbackend-i8fx.onrender.com/send-mail", {
        email: user.email,
        subject: "Your Investment Deposit Request",
        CompanyName: "TRUE LEDGER ASSIST",
        htmlToSend: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
            <div style="text-align: center; margin-bottom: 20px;">
              <img src="https://trueledgerassist.com/public/uploads/logo/light.png" alt="TRUE LEDGER ASSIST Logo" style="height: 60px;" />
              <h2 style="margin: 10px 0; color: #1a1a1a;">TRUE LEDGER ASSIST</h2>
            </div>
            <h3 style="color: #333;">Hi! INVESTOR ${username}</h3>
            <p style="color: #555;">We've received your investment deposit request for the "${selectedPlan.name}" plan:</p>
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Plan:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedPlan.name}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedMethod}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style="color: #212529;">$${parseFloat(amount).toFixed(2)}</span></td>
              </tr>
            </table>
            <p style="color: #555;">Please complete your deposit by sending the amount to the wallet address below:</p>
            <p style="color: #111;"><strong>${selectedMethod}:</strong> ${walletAddresses[selectedMethod] || "Not available"}</p>
            <p style="margin-top: 30px; color: #555;">Once we confirm the payment, your investment will be activated automatically.</p>
            <p style="margin-top: 20px; color: #555;">Thank you for choosing TRUE LEDGER ASSIST!</p>
            <p style="color: #888;">– The TRUE LEDGER ASSIST Team</p>
            <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
            <p style="font-size: 12px; color: #aaa; text-align: center;">
              © 2025 TRUE LEDGER ASSIST. All rights reserved.
            </p>
          </div>
        `,
      });

      setMessages((prev) => ({
        ...prev,
        confirmation: "Deposit request submitted successfully. Please complete the payment to activate your investment.",
      }));
      setIsModalOpen(true);
    }
  } catch (error) {
    console.error("Error processing investment:", error);
    setMessages((prev) => ({
      ...prev,
      confirmation: "Failed to process request: " + error.message,
    }));
  } finally {
    setSubmitting(false);
  }
};

  const copyToClipboard = () => {
    const address = walletAddresses[selectedMethod];
    if (address) {
      navigator.clipboard
        .writeText(address)
        .then(() => {
          setMessages((prev) => ({
            ...prev,
            confirmation: "Wallet address copied!",
          }));
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          setMessages((prev) => ({
            ...prev,
            confirmation: "Failed to copy wallet address.",
          }));
        });
    } else {
      setMessages((prev) => ({
        ...prev,
        confirmation: "No wallet address available.",
      }));
    }
  };

  const handleBack = () => {
    if (step === "funding") {
      setSelectedPlan(null);
      setAmount("");
      setFundingSource("");
      setSelectedMethod("");
      setStep("planSelection");
    } else if (step === "confirmation") {
      setStep("funding");
    }
  };

  const handleRestart = () => {
    setStep("planSelection");
    setSelectedPlan(null);
    setAmount("");
    setFundingSource("");
    setSelectedMethod("");
    setMessages({});
    setIsModalOpen(false);
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>TRUE LEDGER ASSIST | Invest</title>
        <link
          rel="icon"
          href="https://trueledgerassist.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352"
          type="image/png"
        />
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
          integrity="sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN"
          crossOrigin="anonymous"
        />
      </Helmet>

      <style>
        {`
          .custom-select {
            display: inline-block;
            max-width: 100%;
            height: calc(2.25rem + 2px);
            padding: .375rem 1.75rem .375rem .75rem;
            line-height: 1.5;
            color: #0b57e3;
            vertical-align: middle;
            background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
            background-size: 8px 10px;
            border: 4px solid black;
            border-radius: .25rem;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          .custom-select:focus {
            border-color: #80bdff;
            outline: 0;
          }
          .custom-select:disabled {
            color: #868e96;
            background-color: #e9ecef;
          }
          .pricing-table {
            transition: all 0.3s ease;
          }
          .pricing-table:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
          }
          .pricing-features .feature {
            margin-bottom: 10px;
          }
          .pricing-features .feature span {
            color: #212529;
            font-weight: bold;
          }
          .message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
          }
          .message.success {
            background-color: #d4edda;
            color: #155724;
          }
          .message.error {
            background-color: #f8d7da;
            color: #721c24;
          }
          .Toastify__toast--success {
            background-color: #d4edda;
            color: #155724;
          }
          .Toastify__toast--error {
            background-color: #f8d7da;
            color: #721c24;
          }
          .qr-code-container {
            text-align: center;
            margin: 15px 0;
          }
          .wallet-address {
            word-break: break-all;
            margin-top: 10px;
            color: #111;
          }
          .footer-block {
            min-height: 400px;
            padding: 50px 0;
            background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg);
          }
          .foot-header {
            font-family: "Montserrat", Sans-serif;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 25px;
            color: #4A4A4A;
          }
          .foot {
            display: block;
            font-size: small;
            margin-bottom: 8px;
            font-family: "Montserrat", Sans-serif;
            color: #A1A39E !important;
          }
          .foot:hover {
            text-decoration: none !important;
            color: #FE413C !important;
          }
          .foot::before {
            content: "-";
            margin-right: 3px;
            color: #FE413C;
          }
          .footer-credit {
            font-family: "Montserrat", Sans-serif;
            color: #FE413C;
            font-size: small;
          }
          .footer-block .small-image {
            width: 250px;
            height: auto;
          }
        `}
      </style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              <div className="mt-2 mb-4">
                <h1 className="title1 text-dark">Start Your Investment</h1>
                <p className="text-dark">
                  Available Balance:{" "}
                  <span style={{ color: "#212529" }}>
                    ${balance.toFixed(2)}
                  </span>
                </p>
                {messages.global && (
                  <div
                    className={`message ${
                      messages.global.includes("success") ? "success" : "error"
                    }`}
                  >
                    {messages.global}
                  </div>
                )}
              </div>

              {/* Toast Container */}
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="progress" style={{ height: "30px" }}>
                  <div
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{
                      width: `${
                        step === "planSelection"
                          ? 33
                          : step === "funding"
                          ? 66
                          : 100
                      }%`,
                    }}
                    aria-valuenow={
                      step === "planSelection"
                        ? 33
                        : step === "funding"
                        ? 66
                        : 100
                    }
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {step === "planSelection"
                      ? "Step 1: Select Plan"
                      : step === "funding"
                      ? "Step 2: Choose Funding"
                      : "Step 3: Confirm"}
                  </div>
                </div>
              </div>

              {/* Step 1: Plan Selection */}
              {step === "planSelection" && (
                <div className="step-transition">
                  <h3 className="text-dark mb-3">Select an Investment Plan</h3>
                  {loading ? (
                    <div className="text-center">
                      <i className="fas fa-spinner fa-spin fa-2x"></i>{" "}
                      Loading...
                    </div>
                  ) : plans.length === 0 ? (
                    <p className="text-dark">No investment plans available.</p>
                  ) : (
                    <div className="row">
                      {plans.map((plan) => (
                        <div className="col-lg-4 mb-4" key={plan.id}>
                          <div className="pricing-table card border bg-light shadow p-4">
                            <h2 className="text-dark text-center">
                              {plan.name}
                            </h2>
                            <div className="pricing-features">
                              <div className="feature text-dark">
                                Minimum: <span>${plan.min}</span>
                              </div>
                              <div className="feature text-dark">
                                Maximum:{" "}
                                <span>
                                  {plan.max === "Unlimited"
                                    ? "Unlimited"
                                    : `$${plan.max}`}
                                </span>
                              </div>
                              <div className="feature text-dark">
                                Cumulative Profit: <span>{plan.roi}%</span>
                              </div>
                              <div className="feature text-dark">
                                Referral Bonus:{" "}
                                <span>{plan.referralBonus || 5}%</span>
                              </div>
                              <div className="feature text-dark">
                                Duration: <span>{plan.duration}</span>
                              </div>
                            </div>
                            <button
                              className="btn btn-block btn-primary mt-3"
                              onClick={() => handleSelectPlan(plan)}
                            >
                              Choose Plan
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Funding */}
              {step === "funding" && selectedPlan && (
                <div className="step-transition">
                  <h3 className="text-dark mb-3">
                    Choose Funding Source for {selectedPlan.name}
                  </h3>
                  {messages.funding && (
                    <div
                      className={`message ${
                        messages.funding.includes("success")
                          ? "success"
                          : "error"
                      }`}
                    >
                      {messages.funding}
                    </div>
                  )}
                  <form
                    onSubmit={handleFundingSubmit}
                    className="card bg-light shadow p-4"
                  >
                    <div className="mb-3">
                      <h5 className="card-title text-dark">
                        Enter Investment Amount
                      </h5>
                      <input
                        type="number"
                        className="form-control text-dark bg-light"
                        placeholder={`Minimum: $${selectedPlan.min}`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        required
                        min={selectedPlan.min}
                        max={
                          selectedPlan.max !== "Unlimited"
                            ? selectedPlan.max
                            : undefined
                        }
                        step="0.01"
                        aria-label="Investment amount"
                      />
                    </div>
                    <div className="mb-3">
                      <h5 className="card-title text-dark">
                        Select Funding Source
                      </h5>
                      <select
                        className="custom-select"
                        value={fundingSource}
                        onChange={(e) => setFundingSource(e.target.value)}
                        required
                        aria-label="Funding source"
                      >
                        <option value="" disabled>
                          Select funding source
                        </option>
                        <option value="balance">Invest from Balance</option>
                        <option value="paymentMethod">
                          Deposit with Payment Method
                        </option>
                      </select>
                    </div>
                    {fundingSource === "paymentMethod" && (
                      <div className="mb-3">
                        <h5 className="card-title text-dark">
                          Choose Payment Method
                        </h5>
                        <select
                          className="custom-select"
                          value={selectedMethod}
                          onChange={(e) => setSelectedMethod(e.target.value)}
                          required
                          aria-label="Payment method"
                        >
                          <option value="" disabled>
                            Select a payment method
                          </option>
                          {Object.keys(walletAddresses).map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="d-flex justify-content-between">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleBack}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting}
                      >
                        Next
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 3: Confirmation */}
              {step === "confirmation" && selectedPlan && (
                <div className="step-transition">
                  <h3 className="text-dark mb-3">Confirm Your Investment</h3>
                  {messages.confirmation && (
                    <div
                      className={`message ${
                        messages.confirmation.includes("success") ||
                        messages.confirmation.includes("copied")
                          ? "success"
                          : "error"
                      }`}
                    >
                      {messages.confirmation}
                    </div>
                  )}
                  <div className="card bg-light shadow p-4">
                    <h5 className="card-title text-dark">Investment Details</h5>
                    <div className="pricing-features">
                      <div className="feature text-dark">
                        Plan: <span>{selectedPlan.name}</span>
                      </div>
                      <div className="feature text-dark">
                        Amount:{" "}
                        <span style={{ color: "#212529" }}>
                          ${parseFloat(amount).toFixed(2)}
                        </span>
                      </div>
                      <div className="feature text-dark">
                        Funding Source:{" "}
                        <span>
                          {fundingSource === "balance"
                            ? "Balance"
                            : selectedMethod}
                        </span>
                      </div>
                    </div>
                    {fundingSource === "paymentMethod" && (
                      <div className="qr-code-container">
                        <h5 className="card-title text-dark">
                          {selectedMethod} Wallet Address
                        </h5>
                        <QRCodeCanvas
                          value={walletAddresses[selectedMethod] || ""}
                          size={128}
                        />
                        <p className="wallet-address">
                          {walletAddresses[selectedMethod] || "Not available"}
                        </p>
                        <button
                          type="button"
                          className="btn btn-outline-secondary mt-2"
                          onClick={copyToClipboard}
                        >
                          Copy Address
                        </button>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleBack}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleConfirm}
                        disabled={submitting}
                      >
                        {submitting ? "Processing..." : "Confirm Investment"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Restart Button */}
              {step === "confirmation" &&
                messages.confirmation &&
                (messages.confirmation.includes("success") ||
                  messages.confirmation.includes("submitted")) && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleRestart}
                    >
                      Start New Investment
                    </button>
                  </div>
                )}

              {/* Deposit Confirmation Modal */}
              {isModalOpen && (
                <>
                  <div
                    className="modal fade show d-block"
                    id="depositModal"
                    tabIndex="-1"
                    aria-labelledby="depositModalLabel"
                  >
                    <div className="modal-dialog">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title" id="depositModalLabel">
                            Investment Deposit Request Submitted
                          </h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setIsModalOpen(false)}
                            aria-label="Close"
                          ></button>
                        </div>
                        <div className="modal-body">
                          <p>
                            Your investment deposit request for{" "}
                            <span style={{ color: "#212529" }}>
                              ${parseFloat(amount).toFixed(2)}
                            </span>{" "}
                            via{" "}
                            <span style={{ color: "#212529" }}>
                              {selectedMethod}
                            </span>{" "}
                            for the{" "}
                            <span style={{ color: "#212529" }}>
                              {selectedPlan.name}
                            </span>{" "}
                            plan has been submitted.
                          </p>
                          <p>
                            Please send the amount to the following wallet
                            address:
                          </p>
                          <div className="qr-code-container">
                            <QRCodeCanvas
                              value={walletAddresses[selectedMethod] || ""}
                              size={128}
                            />
                            <p className="wallet-address">
                              {walletAddresses[selectedMethod] ||
                                "Not available"}
                            </p>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={copyToClipboard}
                            >
                              Copy Address
                            </button>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setIsModalOpen(false)}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="modal-backdrop fade show"
                    onClick={() => setIsModalOpen(false)}
                  ></div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="footer-block">
              <div className="container">
                <div className="row">
                  <div className="col-6 col-md-4 mb-3 mb-md-0">
                    <div className="foot-header">Company</div>
                    <div className="foot-body">
                      <a href="/dashboards/buy-plan" className="foot">
                        Investment Plans
                      </a>
                      <a href="/about.html" className="foot">
                        About Us
                      </a>
                      <a
                        href="https://www.google.com/maps?ll=-27.382143,152.993196&z=9&t=m&hl=en&gl=NG&mapclient=embed&q=Brisbane+Queensland+Australia"
                        className="foot"
                      >
                        <b>Australia Address:</b> Brisbane
                      </a>
                      <a href="/contact.html" className="foot">
                        Contact us
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Platforms</div>
                    <div className="foot-body">
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Platforms
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        MT4 for Windows
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        MT4 for iOS
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        MT4 for Android
                      </a>
                      <a
                        href="https://mine.mql5.com/mine?servers=-Demo%2CprimeMiningNetwork-Live&mine_server=primeMiningNetwork-Demo&demo_server=primeMiningNetwork-Demo&startup_mode=open_demo&lang=en"
                        className="foot"
                      >
                        Web miner
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Trading</div>
                    <div className="foot-body">
                      <a
                        href="https://trueledgerassist.com/auth/register"
                        className="foot"
                      >
                        Open Live Account
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Instruments
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Leaderboard
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Market Analysis
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Funding</div>
                    <div className="foot-body">
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Funding Methods
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Clients Protection
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Legal</div>
                    <div className="foot-body">
                      <a href="#" className="foot">
                        AML Policy
                      </a>
                      <a href="#" className="foot">
                        Cookie Policy
                      </a>
                      <a href="#" className="foot">
                        Privacy Policy
                      </a>
                      <a href="/faq.html" className="foot">
                        Risk Disclosure
                      </a>
                      <a href="/terms.html" className="foot">
                        Terms & Conditions
                      </a>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-5">
                  <img
                    src="/home/logo-payments.png"
                    className="small-image"
                    alt="Payment Methods"
                  />
                </div>
                <div className="text-center mt-4">
                  <p className="footer-credit">
                    ©2025, TRUE LEDGER ASSIST - ALL RIGHTS RESERVED.
                  </p>
                </div>
              </div>
            </div>
            <div
              style={{
                position: "fixed",
                left: 0,
                bottom: 0,
                zIndex: 1,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                  boxSizing: "content-box",
                  textAlign: "right",
                  lineHeight: "14px",
                  fontSize: "12px",
                  fontFeatureSettings: "normal",
                  textSizeAdjust: "100%",
                  padding: "0px",
                  margin: "0px",
                }}
              >
                <div style={{ height: "40px" }}>
                  <iframe
                    src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
                    width="100%"
                    height={36}
                    scrolling="auto"
                    marginWidth={0}
                    marginHeight={0}
                    frameBorder={0}
                    border={0}
                    style={{ border: 0, margin: 0, padding: 0 }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestmentFlow;

// import React, { useState, useEffect } from "react";
// import { Helmet } from "react-helmet";
// import { useNavigate } from "react-router-dom";
// import { db, auth } from "../config";
// import {
//   doc,
//   getDoc,
//   addDoc,
//   collection,
//   updateDoc,
//   getDocs,
//   query,
//   where,
//   setDoc
// } from "firebase/firestore";
// import { onAuthStateChanged } from "firebase/auth";
// import { QRCodeCanvas } from "qrcode.react";
// import axios from "axios";
// import Nav from "../Components/Nav";

// function InvestmentFlow() {
//   const [step, setStep] = useState("planSelection"); // planSelection, funding, confirmation
//   const [plans, setPlans] = useState([]);
//   const [selectedPlan, setSelectedPlan] = useState(null);
//   const [amount, setAmount] = useState("");
//   const [fundingSource, setFundingSource] = useState(""); // balance or paymentMethod
//   const [selectedMethod, setSelectedMethod] = useState("");
//   const [walletAddresses, setWalletAddresses] = useState({});
//   const [user, setUser] = useState(null);
//   const [balance, setBalance] = useState(0);
//   const [loading, setLoading] = useState(true);
//   const [submitting, setSubmitting] = useState(false);
//   const [messages, setMessages] = useState({}); // Step-specific messages
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
//       if (currentUser) {
//         setUser(currentUser);
//         try {
//           const userDocRef = doc(db, "users", currentUser.uid);
//           const userDoc = await getDoc(userDocRef);
//           if (userDoc.exists()) {
//             setBalance(userDoc.data().balance || 0);
//           }

//           const walletRef = doc(db, "settings", "walletAddresses");
//           const walletSnapshot = await getDoc(walletRef);
//           if (walletSnapshot.exists()) {
//             setWalletAddresses(walletSnapshot.data());
//           }

//           const querySnapshot = await getDocs(collection(db, "investmentPlans"));
//           const fetchedPlans = querySnapshot.docs.map((doc) => ({
//             id: doc.id,
//             ...doc.data(),
//           }));
//           setPlans(fetchedPlans);
//         } catch (error) {
//           console.error("Error fetching data:", error);
//           setMessages({ global: "Error fetching user data or plans." });
//         } finally {
//           setLoading(false);
//         }
//       } else {
//         setUser(null);
//         navigate("/auth/login");
//       }
//     });

//     return () => unsubscribe();
//   }, [navigate]);

//   useEffect(() => {
//     // Clear messages after 3 seconds
//     if (messages[step]) {
//       const timer = setTimeout(() => {
//         setMessages((prev) => ({ ...prev, [step]: "" }));
//       }, 3000);
//       return () => clearTimeout(timer);
//     }
//   }, [messages, step]);

//   const handleSelectPlan = (plan) => {
//     setSelectedPlan(plan);
//     setAmount(plan.min.toString()); // Set default amount to plan minimum
//     setStep("funding");
//   };

//   const handleFundingSubmit = (e) => {
//     e.preventDefault();
//     const investmentAmount = parseFloat(amount);

//     if (!fundingSource) {
//       setMessages((prev) => ({ ...prev, funding: "Please select a funding source." }));
//       return;
//     }

//     if (isNaN(investmentAmount) || investmentAmount <= 0) {
//       setMessages((prev) => ({ ...prev, funding: "Please enter a valid amount." }));
//       return;
//     }

//     if (investmentAmount < selectedPlan.min) {
//       setMessages((prev) => ({
//         ...prev,
//         funding: `Amount must be at least $${selectedPlan.min}.`,
//       }));
//       return;
//     }

//     if (selectedPlan.max !== "Unlimited" && investmentAmount > selectedPlan.max) {
//       setMessages((prev) => ({
//         ...prev,
//         funding: `Amount cannot exceed $${selectedPlan.max}.`,
//       }));
//       return;
//     }

//     if (fundingSource === "paymentMethod" && !selectedMethod) {
//       setMessages((prev) => ({ ...prev, funding: "Please select a payment method." }));
//       return;
//     }

//     setStep("confirmation");
//   };

//   const handleConfirm = async () => {
//     setSubmitting(true);
//     const investmentAmount = parseFloat(amount);

//     try {
//       // Check for existing active investment
//       const investmentsRef = collection(db, "investments");
//       const activeQuery = query(
//         investmentsRef,
//         where("userId", "==", user.uid),
//         where("plan", "==", selectedPlan.name),
//         where("status", "==", "active")
//       );
//       const activeSnapshot = await getDocs(activeQuery);

//       if (!activeSnapshot.empty) {
//         setMessages((prev) => ({
//           ...prev,
//           confirmation: `You already have an active investment in the "${selectedPlan.name}" plan.`,
//         }));
//         setSubmitting(false);
//         return;
//       }

//       // Create investment document
//       const investmentData = {
//         userId: user.uid,
//         email: user.email,
//         plan: selectedPlan.name,
//         roi: selectedPlan.roi,
//         duration: selectedPlan.duration,
//         amount: investmentAmount,
//         createdAt: new Date(),
//         status: fundingSource === "balance" ? "active" : "unpaid",
//       };

//       const investmentDoc = await addDoc(collection(db, "investments"), investmentData);
//       const investmentId = investmentDoc.id;

//       if (fundingSource === "balance") {
//         // Check balance
//         if (investmentAmount > balance) {
//           setMessages((prev) => ({ ...prev, confirmation: "Insufficient balance." }));
//           setSubmitting(false);
//           return;
//         }

//         // Update user balance
//         const userDocRef = doc(db, "users", user.uid);
//         const newBalance = balance - investmentAmount;
//         await updateDoc(userDocRef, { balance: newBalance });
//         setBalance(newBalance);

//         setMessages((prev) => ({
//           ...prev,
//           confirmation: "Investment activated successfully!",
//         }));
//       } else {
//         // Create linked deposit
//         const depositData = {
//           userId: user.uid,
//           userEmail: user.email,
//           method: selectedMethod,
//           amount: investmentAmount,
//           timestamp: new Date().toISOString(),
//           status: "pending",
//           investmentId: investmentId,
//         };

//         await setDoc(doc(db, "deposits", `${user.uid}_${Date.now()}`), depositData);

//         // Send deposit request email
//         await axios.post("https://renderbackend-i8fx.onrender.com/send-mail", {
//           email: user.email,
//           subject: "Your Investment Deposit Request",
//           CompanyName: "TRUE LEDGER ASSIST",
//           htmlToSend: `
//             <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
//               <div style="text-align: center; margin-bottom: 20px;">
//                 <img src="https://trueledgerassist.com/public/uploads/logo/light.png" alt="TRUE LEDGER ASSIST Logo" style="height: 60px;" />
//                 <h2 style="margin: 10px 0; color: #1a1a1a;">TRUE LEDGER ASSIST</h2>
//               </div>
//               <h3 style="color: #333;">Hi!</h3>
//               <p style="color: #555;">We've received your investment deposit request for the "${selectedPlan.name}" plan:</p>
//               <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
//                 <tr>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Plan:</strong></td>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedPlan.name}</td>
//                 </tr>
//                 <tr>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedMethod}</td>
//                 </tr>
//                 <tr>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style={{ color: "#212529" }}>${parseFloat(amount).toFixed(2)}</span></td>
//                 </tr>
//               </table>
//               <p style="color: #555;">Please complete your deposit by sending the amount to the wallet address below:</p>
//               <p style="color: #111;"><strong>${selectedMethod}:</strong> ${walletAddresses[selectedMethod] || "Not available"}</p>
//               <p style="margin-top: 30px; color: #555;">Once we confirm the payment, your investment will be activated.</p>
//               <p style="margin-top: 20px; color: #555;">Thank you for choosing TRUE LEDGER ASSIST!</p>
//               <p style="color: #888;">– The TRUE LEDGER ASSIST Team</p>
//               <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
//               <p style="font-size: 12px; color: #aaa; text-align: center;">
//                 © ${new Date().getFullYear()} TRUE LEDGER ASSIST. All rights reserved.
//               </p>
//             </div>
//           `,
//         });

//         setIsModalOpen(true);
//       }

//       setMessages((prev) => ({
//         ...prev,
//         confirmation: fundingSource === "balance" ? "Investment activated successfully!" : "Deposit request submitted. Please complete the payment.",
//       }));
//     } catch (error) {
//       console.error("Error processing investment:", error);
//       setMessages((prev) => ({ ...prev, confirmation: "Failed to process investment: " + error.message }));
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const copyToClipboard = () => {
//     const address = walletAddresses[selectedMethod];
//     if (address) {
//       navigator.clipboard.writeText(address).then(() => {
//         setMessages((prev) => ({ ...prev, confirmation: "Wallet address copied!" }));
//       }).catch((err) => {
//         console.error("Failed to copy: ", err);
//         setMessages((prev) => ({ ...prev, confirmation: "Failed to copy wallet address." }));
//       });
//     } else {
//       setMessages((prev) => ({ ...prev, confirmation: "No wallet address available." }));
//     }
//   };

//   const handleBack = () => {
//     if (step === "funding") {
//       setSelectedPlan(null);
//       setAmount("");
//       setFundingSource("");
//       setSelectedMethod("");
//       setStep("planSelection");
//     } else if (step === "confirmation") {
//       setStep("funding");
//     }
//   };

//   const handleRestart = () => {
//     setStep("planSelection");
//     setSelectedPlan(null);
//     setAmount("");
//     setFundingSource("");
//     setSelectedMethod("");
//     setMessages({});
//     setIsModalOpen(false);
//   };

//   return (
//     <div>
//       <Helmet>
//         <meta charSet="utf-8" />
//         <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
//         <meta name="viewport" content="width=device-width, initial-scale=1" />
//         <title>TRUE LEDGER ASSIST | Invest</title>
//         <link
//           rel="icon"
//           href="https://trueledgerassist.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352"
//           type="image/png"
//         />
//         <link
//           rel="stylesheet"
//           href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
//           integrity="sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN"
//           crossOrigin="anonymous"
//         />
//       </Helmet>

//       <div className="wrapper">
//         <Nav />
//         <div className="main-panel bg-light">
//           <div className="content bg-light">
//             <div className="page-inner">
//               <div className="mt-2 mb-4">
//                 <h1 className="title1 text-dark">Start Your Investment</h1>
//                 <p className="text-dark">Available Balance: <span style={{ color: "#212529" }}>${balance.toFixed(2)}</span></p>
//                 {messages.global && (
//                   <div className={`message ${messages.global.includes("success") ? "success" : "error"}`}>
//                     {messages.global}
//                   </div>
//                 )}
//               </div>

//               {/* Progress Bar */}
//               <div className="mb-4">
//                 <div className="progress" style={{ height: "30px" }}>
//                   <div
//                     className="progress-bar bg-primary"
//                     role="progressbar"
//                     style={{
//                       width: `${
//                         step === "planSelection" ? 33 : step === "funding" ? 66 : 100
//                       }%`,
//                     }}
//                     aria-valuenow={step === "planSelection" ? 33 : step === "funding" ? 66 : 100}
//                     aria-valuemin="0"
//                     aria-valuemax="100"
//                   >
//                     {step === "planSelection"
//                       ? "Step 1: Select Plan"
//                       : step === "funding"
//                       ? "Step 2: Choose Funding"
//                       : "Step 3: Confirm"}
//                   </div>
//                 </div>
//               </div>

//               {/* Step 1: Plan Selection */}
//               {step === "planSelection" && (
//                 <div className="step-transition">
//                   <h3 className="text-dark mb-3">Select an Investment Plan</h3>
//                   {loading ? (
//                     <div className="text-center">
//                       <i className="fas fa-spinner fa-spin fa-2x"></i> Loading...
//                     </div>
//                   ) : plans.length === 0 ? (
//                     <p className="text-dark">No investment plans available.</p>
//                   ) : (
//                     <div className="row">
//                       {plans.map((plan) => (
//                         <div className="col-lg-4 mb-4" key={plan.id}>
//                           <div className="pricing-table card border bg-light shadow p-4">
//                             <h2 className="text-dark text-center">{plan.name}</h2>
//                             <div className="pricing-features">
//                               <div className="feature text-dark">
//                                 Minimum: <span>${plan.min}</span>
//                               </div>
//                               <div className="feature text-dark">
//                                 Maximum: <span>{plan.max === "Unlimited" ? "Unlimited" : `$${plan.max}`}</span>
//                               </div>
//                               <div className="feature text-dark">
//                                 Cumulative Profit: <span>{plan.roi}%</span>
//                               </div>
//                               <div className="feature text-dark">
//                                 Referral Bonus: <span>{plan.referralBonus || 5}%</span>
//                               </div>
//                               <div className="feature text-dark">
//                                 Duration: <span>{plan.duration}</span>
//                               </div>
//                             </div>
//                             <button
//                               className="btn btn-block btn-primary mt-3"
//                               onClick={() => handleSelectPlan(plan)}
//                             >
//                               Choose Plan
//                             </button>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )}

//               {/* Step 2: Funding */}
//               {step === "funding" && selectedPlan && (
//                 <div className="step-transition">
//                   <h3 className="text-dark mb-3">Choose Funding Source for {selectedPlan.name}</h3>
//                   {messages.funding && (
//                     <div className={`message ${messages.funding.includes("success") ? "success" : "error"}`}>
//                       {messages.funding}
//                     </div>
//                   )}
//                   <form onSubmit={handleFundingSubmit} className="card bg-light shadow p-4">
//                     <div className="mb-3">
//                       <h5 className="card-title text-dark">Enter Investment Amount</h5>
//                       <input
//                         type="number"
//                         className="form-control text-dark bg-light"
//                         placeholder={`Minimum: $${selectedPlan.min}`}
//                         value={amount}
//                         onChange={(e) => setAmount(e.target.value)}
//                         required
//                         min={selectedPlan.min}
//                         max={selectedPlan.max !== "Unlimited" ? selectedPlan.max : undefined}
//                         step="0.01"
//                         aria-label="Investment amount"
//                       />
//                     </div>
//                     <div className="mb-3">
//                       <h5 className="card-title text-dark">Select Funding Source</h5>
//                       <select
//                         className="custom-select"
//                         value={fundingSource}
//                         onChange={(e) => setFundingSource(e.target.value)}
//                         required
//                         aria-label="Funding source"
//                       >
//                         <option value="" disabled>Select funding source</option>
//                         <option value="balance">Invest from Balance</option>
//                         <option value="paymentMethod">Deposit with Payment Method</option>
//                       </select>
//                     </div>
//                     {fundingSource === "paymentMethod" && (
//                       <div className="mb-3">
//                         <h5 className="card-title text-dark">Choose Payment Method</h5>
//                         <select
//                           className="custom-select"
//                           value={selectedMethod}
//                           onChange={(e) => setSelectedMethod(e.target.value)}
//                           required
//                           aria-label="Payment method"
//                         >
//                           <option value="" disabled>Select a payment method</option>
//                           {Object.keys(walletAddresses).map((method) => (
//                             <option key={method} value={method}>{method}</option>
//                           ))}
//                         </select>
//                       </div>
//                     )}
//                     <div className="d-flex justify-content-between">
//                       <button
//                         type="button"
//                         className="btn btn-outline-secondary"
//                         onClick={handleBack}
//                       >
//                         Back
//                       </button>
//                       <button
//                         type="submit"
//                         className="btn btn-primary"
//                         disabled={submitting}
//                       >
//                         Next
//                       </button>
//                     </div>
//                   </form>
//                 </div>
//               )}

//               {/* Step 3: Confirmation */}
//               {step === "confirmation" && selectedPlan && (
//                 <div className="step-transition">
//                   <h3 className="text-dark mb-3">Confirm Your Investment</h3>
//                   {messages.confirmation && (
//                     <div className={`message ${messages.confirmation.includes("success") || messages.confirmation.includes("copied") ? "success" : "error"}`}>
//                       {messages.confirmation}
//                     </div>
//                   )}
//                   <div className="card bg-light shadow p-4">
//                     <h5 className="card-title text-dark">Investment Details</h5>
//                     <div className="pricing-features">
//                       <div className="feature text-dark">
//                         Plan: <span>{selectedPlan.name}</span>
//                       </div>
//                       <div className="feature text-dark">
//                         Amount: <span style={{ color: "#212529" }}>${parseFloat(amount).toFixed(2)}</span>
//                       </div>
//                       <div className="feature text-dark">
//                         Funding Source: <span>{fundingSource === "balance" ? "Balance" : selectedMethod}</span>
//                       </div>
//                     </div>
//                     {fundingSource === "paymentMethod" && (
//                       <div className="qr-code-container">
//                         <h5 className="card-title text-dark">{selectedMethod} Wallet Address</h5>
//                         <QRCodeCanvas value={walletAddresses[selectedMethod] || ""} size={128} />
//                         <p className="wallet-address">{walletAddresses[selectedMethod] || "Not available"}</p>
//                         <button
//                           type="button"
//                           className="btn btn-outline-secondary mt-2"
//                           onClick={copyToClipboard}
//                         >
//                           Copy Address
//                         </button>
//                       </div>
//                     )}
//                     <div className="d-flex justify-content-between mt-3">
//                       <button
//                         type="button"
//                         className="btn btn-outline-secondary"
//                         onClick={handleBack}
//                       >
//                         Back
//                       </button>
//                       <button
//                         type="button"
//                         className="btn btn-primary"
//                         onClick={handleConfirm}
//                         disabled={submitting}
//                       >
//                         {submitting ? "Processing..." : "Confirm Investment"}
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {/* Restart Button */}
//               {(step === "confirmation" && messages.confirmation && messages.confirmation.includes("success")) && (
//                 <div className="text-center mt-3">
//                   <button
//                     className="btn btn-outline-primary"
//                     onClick={handleRestart}
//                   >
//                     Start New Investment
//                   </button>
//                 </div>
//               )}
//             </div>

//             {/* Footer */}
//             <div className="footer-block">
//               <div className="container">
//                 <div className="row">
//                   <div className="col-6 col-md-4 mb-3 mb-md-0">
//                     <div className="foot-header">Company</div>
//                     <div className="foot-body">
//                       <a href="/dashboards/buy-plan" className="foot">Investment Plans</a>
//                       <a href="/about.html" className="foot">About Us</a>
//                       <a
//                         href="https://www.google.com/maps?ll=-27.382143,152.993196&z=9&t=m&hl=en&gl=NG&mapclient=embed&q=Brisbane+Queensland+Australia"
//                         className="foot"
//                       >
//                         <b>Australia Address:</b> Brisbane
//                       </a>
//                       <a href="/contact.html" className="foot">Contact us</a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Platforms</div>
//                     <div className="foot-body">
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">Platforms</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">MT4 for Windows</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">MT4 for iOS</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">MT4 for Android</a>
//                       <a
//                         href="https://mine.mql5.com/mine?servers=-Demo%2CprimeMiningNetwork-Live&mine_server=primeMiningNetwork-Demo&demo_server=primeMiningNetwork-Demo&startup_mode=open_demo&lang=en"
//                         className="foot"
//                       >
//                         Web miner
//                       </a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Trading</div>
//                     <div className="foot-body">
//                       <a href="https://trueledgerassist.com/auth/register" className="foot">Open Live Account</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">Instruments</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">Leaderboard</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">Market Analysis</a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Funding</div>
//                     <div className="foot-body">
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">Funding Methods</a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">Clients Protection</a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Legal</div>
//                     <div className="foot-body">
//                       <a href="#" className="foot">AML Policy</a>
//                       <a href="#" className="foot">Cookie Policy</a>
//                       <a href="#" className="foot">Privacy Policy</a>
//                       <a href="/faq.html" className="foot">Risk Disclosure</a>
//                       <a href="/terms.html" className="foot">Terms & Conditions</a>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="text-center mt-5">
//                   <img src="/home/logo-payments.png" className="small-image" alt="Payment Methods" />
//                 </div>
//                 <div className="text-center mt-4">
//                   <p className="footer-credit">©2025, TRUE LEDGER ASSIST - ALL RIGHTS RESERVED.</p>
//                 </div>
//               </div>
//             </div>
//             <div
//               style={{
//                 position: "fixed",
//                 left: 0,
//                 bottom: 0,
//                 zIndex: 1,
//                 width: "100%",
//               }}
//             >
//               <div
//                 style={{
//                   width: "100%",
//                   backgroundColor: "#FFFFFF",
//                   overflow: "hidden",
//                   boxSizing: "content-box",
//                   textAlign: "right",
//                   lineHeight: "14px",
//                   fontSize: "12px",
//                   fontFeatureSettings: "normal",
//                   textSizeAdjust: "100%",
//                   padding: "0px",
//                   margin: "0px",
//                 }}
//               >
//                 <div style={{ height: "40px" }}>
//                   <iframe
//                     src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
//                     width="100%"
//                     height={36}
//                     scrolling="auto"
//                     marginWidth={0}
//                     marginHeight={0}
//                     frameBorder={0}
//                     border={0}
//                     style={{ border: 0, margin: 0, padding: 0 }}
//                   ></iframe>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Deposit Confirmation Modal */}
//       {isModalOpen && (
//         <>
//           <div className="modal fade show d-block" id="depositModal" tabIndex="-1" aria-labelledby="depositModalLabel">
//             <div className="modal-dialog">
//               <div className="modal-content">
//                 <div className="modal-header">
//                   <h5 className="modal-title" id="depositModalLabel">Investment Deposit Request Submitted</h5>
//                   <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
//                 </div>
//                 <div className="modal-body">
//                   <p>Your investment deposit request for <span style={{ color: "#212529" }}>${parseFloat(amount).toFixed(2)}</span> via <span style={{ color: "#212529" }}>{selectedMethod}</span> for the <span style={{ color: "#212529" }}>{selectedPlan.name}</span> plan has been submitted.</p>
//                   <p>Please send the amount to the following wallet address:</p>
//                   <div className="qr-code-container">
//                     <QRCodeCanvas value={walletAddresses[selectedMethod] || ""} size={128} />
//                     <p className="wallet-address">{walletAddresses[selectedMethod] || "Not available"}</p>
//                     <button type="button" className="btn btn-outline-secondary" onClick={copyToClipboard}>
//                       Copy Address
//                     </button>
//                   </div>
//                 </div>
//                 <div className="modal-footer">
//                   <button type="button" className="btn btn-primary" onClick={() => setIsModalOpen(false)}>
//                     Close
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="modal-backdrop fade show" onClick={() => setIsModalOpen(false)}></div>
//         </>
//       )}
//     </div>
//   );
// }

// export default InvestmentFlow;
